import React, { useState } from 'react';
import { useLazyComponent, useLazyComponentOnVisible } from '../../hooks/useLazyComponent';
import { featureLoader } from '../../utils/dynamicFeatureLoader';
import { routePreloader } from '../../utils/routePreloader';
import { performanceMonitor } from '../../utils/performanceMonitor';
import BundlePerformanceMonitor from '../dev/BundlePerformanceMonitor';
import './CodeSplittingTestPage.css';

const CodeSplittingTestPage: React.FC = () => {
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false);
  const [loadedComponents, setLoadedComponents] = useState<string[]>([]);

  // Test lazy loading with different triggers
  const {
    Component: LazyChart,
    loading: chartLoading,
    error: chartError,
    preload: preloadChart
  } = useLazyComponent('interactive-chart');

  const {
    Component: LazyTable,
    loading: tableLoading,
    ref: tableRef
  } = useLazyComponentOnVisible('data-table');

  const handleLoadFeature = async (featureName: string) => {
    try {
      await featureLoader.loadFeature(featureName);
      setLoadedComponents(prev => [...prev, featureName]);
    } catch (error) {
      console.error(`Failed to load feature ${featureName}:`, error);
    }
  };

  const handlePreloadRoute = (path: string) => {
    routePreloader.preloadRoute(path, 'hover');
  };

  const getPerformanceStats = () => {
    const performanceData = performanceMonitor.getPerformanceSummary();
    const routeStats = routePreloader.getStats();
    const bundleInfo = featureLoader.getBundleInfo();

    return {
      performance: performanceData.summary,
      routes: routeStats,
      bundles: bundleInfo
    };
  };

  const stats = getPerformanceStats();

  return (
    <div className="code-splitting-test-page">
      <div className="test-header">
        <h1>Code Splitting & Lazy Loading Test</h1>
        <p>This page demonstrates the code splitting and lazy loading implementation.</p>
        
        <div className="performance-toggle">
          <button 
            onClick={() => setShowPerformanceMonitor(!showPerformanceMonitor)}
            className="performance-btn"
          >
            {showPerformanceMonitor ? 'Hide' : 'Show'} Performance Monitor
          </button>
          <small>Or press Ctrl+Shift+P</small>
        </div>
      </div>

      <div className="test-sections">
        {/* Route Preloading Test */}
        <section className="test-section">
          <h2>Route Preloading</h2>
          <div className="route-stats">
            <div className="stat">
              <span>Total Routes:</span>
              <span>{stats.routes.totalRoutes}</span>
            </div>
            <div className="stat">
              <span>Preloaded:</span>
              <span>{stats.routes.preloadedRoutes}</span>
            </div>
            <div className="stat">
              <span>Currently Loading:</span>
              <span>{stats.routes.preloadingRoutes}</span>
            </div>
          </div>
          
          <div className="route-actions">
            <button onClick={() => handlePreloadRoute('/candidates')}>
              Preload Candidates Page
            </button>
            <button onClick={() => handlePreloadRoute('/jobs')}>
              Preload Jobs Page
            </button>
            <button onClick={() => handlePreloadRoute('/agents')}>
              Preload Agents Page
            </button>
          </div>

          <div className="preloaded-routes">
            <h3>Preloaded Routes:</h3>
            <ul>
              {stats.routes.preloadedPaths.map(path => (
                <li key={path}>{path}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Dynamic Feature Loading Test */}
        <section className="test-section">
          <h2>Dynamic Feature Loading</h2>
          <div className="bundle-stats">
            <div className="stat">
              <span>Registered Features:</span>
              <span>{stats.bundles.registered.length}</span>
            </div>
            <div className="stat">
              <span>Loaded Features:</span>
              <span>{stats.bundles.loaded.length}</span>
            </div>
            <div className="stat">
              <span>Loading Features:</span>
              <span>{stats.bundles.loading.length}</span>
            </div>
          </div>

          <div className="feature-actions">
            <button onClick={() => handleLoadFeature('advanced-analytics')}>
              Load Advanced Analytics
            </button>
            <button onClick={() => handleLoadFeature('data-visualization')}>
              Load Data Visualization
            </button>
            <button onClick={() => handleLoadFeature('report-builder')}>
              Load Report Builder
            </button>
          </div>

          <div className="loaded-features">
            <h3>Loaded Features:</h3>
            <ul>
              {loadedComponents.map(feature => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Component Lazy Loading Test */}
        <section className="test-section">
          <h2>Component Lazy Loading</h2>
          
          <div className="lazy-component-test">
            <h3>Hover to Load Chart</h3>
            <button 
              onMouseEnter={preloadChart}
              className="hover-load-btn"
            >
              Hover me to preload chart
            </button>
            
            {chartLoading && <p>Loading chart component...</p>}
            {chartError && <p>Error loading chart: {chartError.message}</p>}
            {LazyChart && (
              <div className="lazy-component">
                <LazyChart />
              </div>
            )}
          </div>

          <div className="lazy-component-test">
            <h3>Scroll to Load Table</h3>
            <div className="scroll-spacer">Scroll down to load the table component...</div>
            <div ref={tableRef} className="intersection-target">
              {tableLoading && <p>Loading table component...</p>}
              {LazyTable && (
                <div className="lazy-component">
                  <LazyTable />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Performance Metrics */}
        <section className="test-section">
          <h2>Performance Metrics</h2>
          <div className="performance-stats">
            <div className="stat">
              <span>Average Chunk Load Time:</span>
              <span>{stats.performance.averageChunkLoadTime.toFixed(2)}ms</span>
            </div>
            <div className="stat">
              <span>Total Chunks Loaded:</span>
              <span>{stats.performance.totalChunksLoaded}</span>
            </div>
            <div className="stat">
              <span>Cached Chunks:</span>
              <span>{stats.performance.cachedChunks}</span>
            </div>
            <div className="stat">
              <span>Average Route Change Time:</span>
              <span>{stats.performance.averageRouteChangeTime.toFixed(2)}ms</span>
            </div>
          </div>

          <div className="core-web-vitals">
            <h3>Core Web Vitals</h3>
            <div className="vitals-grid">
              {Object.entries(stats.performance.coreWebVitals).map(([key, value]) => (
                <div key={key} className="vital">
                  <span className="vital-name">{key}</span>
                  <span className="vital-value">{value.toFixed(2)}ms</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Performance Monitor */}
      <BundlePerformanceMonitor
        isVisible={showPerformanceMonitor}
        onClose={() => setShowPerformanceMonitor(false)}
      />
    </div>
  );
};

export default CodeSplittingTestPage;